import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { ApiError } from './http';

/** Contract: 501 and other non-retryable errors are designed states, not flakes. */
const retry = (failureCount: number, error: unknown) =>
  error instanceof ApiError ? error.retryable && failureCount < 2 : failureCount < 1;

/**
 * Whether a query is genuinely in flight, as opposed to disabled.
 *
 * React Query reports `status: 'pending'` for a gated query that has never
 * run, so a screen reading `isPending` alone draws a loading state for a
 * request it has decided not to make. `fetchStatus` is the discriminator.
 */
export function isReallyLoading(q: { isPending: boolean; fetchStatus: string }): boolean {
  return q.isPending && q.fetchStatus !== 'idle';
}

export function useBuckets(enabled = true) {
  return useQuery({
    queryKey: ['buckets'],
    queryFn: ({ signal }) => api.listBuckets(signal),
    retry,
    enabled,
    staleTime: 30_000,
  });
}

export function useBucket(id: string | undefined) {
  return useQuery({
    queryKey: ['bucket', id],
    queryFn: ({ signal }) => api.getBucket(id!, signal),
    // Without this the root default retries once, so a designed refusal
    // (FORBIDDEN, NOT_FOUND, a capability that does not exist) is asked
    // again with backoff before the operator is told what happened.
    retry,
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useGraph(id: string | undefined) {
  return useQuery({
    queryKey: ['graph', id],
    queryFn: ({ signal }) => api.getGraph(id!, signal),
    retry,
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useCompanies(enabled: boolean) {
  return useQuery({
    queryKey: ['companies'],
    queryFn: async ({ signal }) => (await import('./backend')).backend.listAllCompanies(signal),
    retry,
    enabled,
    staleTime: 30_000,
  });
}

export function useCompanyTools(companyId: string | undefined) {
  return useQuery({
    queryKey: ['company-tools', companyId],
    queryFn: async ({ signal }) => (await import('./backend')).backend.getCompanyTools(companyId!, signal),
    retry,
    enabled: Boolean(companyId),
    staleTime: 30_000,
  });
}

/**
 * This bucket's detections and nothing else.
 *
 * Replaces reading the company's inventory and filtering to `bucketId` here,
 * which was the last place another bucket's data reached this client at all.
 */
export function useBucketToolDetections(bucketId: string | undefined) {
  return useQuery({
    queryKey: ['bucket-tool-detections', bucketId],
    queryFn: async ({ signal }) => (await import('./backend')).backend.getBucketToolDetections(bucketId!, signal),
    retry,
    enabled: Boolean(bucketId),
    staleTime: 30_000,
  });
}

export function useCapabilities() {
  return useQuery({
    queryKey: ['capabilities'],
    queryFn: ({ signal }) => api.getCapabilities(signal),
    retry,
    staleTime: 60_000,
  });
}
